import React from 'react';
import { Users, GraduationCap, Bus, Moon, BrainCircuit, Package, Warehouse, Leaf, Lock, Globe2 } from 'lucide-react';

const roadmap = [
  {
    year: '2026',
    label: 'Core Modules — Fleet & Mobility',
    color: 'bg-primary-600',
    textColor: 'text-primary-600',
    borderColor: 'border-primary-200',
    bgColor: 'bg-primary-50',
    arrowColor: '#16a34a',
    items: [
      { quarter: 'Q1', icon: GraduationCap, status: 'In Progress', statusColor: 'bg-teal-100 text-teal-700', title: 'SCMT — School & College Transport', desc: 'Parent app, live tracking, QR boarding, 137 features, AI route optimisation.' },
      { quarter: 'Q2', icon: Bus, status: 'Planned', statusColor: 'bg-blue-100 text-blue-700', title: 'Spot Booking — On-Demand Transport', desc: 'Instant booking, AI vehicle assignment, dynamic pricing, in-app payments.' },
      { quarter: 'Q3', icon: Users, status: 'Planned', statusColor: 'bg-blue-100 text-blue-700', title: 'ETS — Employee Transport Service', desc: 'Shift rostering, AI route optimisation, employee self-booking, compliance reporting.' },
      { quarter: 'Q4', icon: Moon, status: 'Planned', statusColor: 'bg-blue-100 text-blue-700', title: 'Night Riders — Nighttime Fleet Ops', desc: 'AI fatigue detection, night risk scoring, dashcam integration, harsh event alerts.' },
    ]
  },
  {
    year: '2027',
    label: 'AI Platform & New Business Lines',
    color: 'bg-purple-600',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
    bgColor: 'bg-purple-50',
    arrowColor: '#9333ea',
    items: [
      { quarter: 'Q1', icon: BrainCircuit, status: 'AI Feature', statusColor: 'bg-purple-100 text-purple-700', title: 'AI Intelligence Engine', desc: 'Predictive maintenance 85–95% accuracy, 20–45 days ahead alerts, digital twin simulation.' },
      { quarter: 'Q2', icon: Package, status: 'Future', statusColor: 'bg-gray-100 text-gray-600', title: 'Parcel & Last-Mile Delivery', desc: 'B2C/B2B parcel management, proof of delivery, COD, AI last-mile route batching.' },
      { quarter: 'Q3', icon: Warehouse, status: 'Future', statusColor: 'bg-gray-100 text-gray-600', title: 'Warehouse Management (WMS)', desc: 'Inbound/outbound workflows, fleet–warehouse sync, AI demand forecasting.' },
      { quarter: 'Q4', icon: Leaf, status: 'Future', statusColor: 'bg-gray-100 text-gray-600', title: 'EV Fleet & Sustainability', desc: 'Battery health, smart charge scheduling, carbon tracking, ESG reporting.' },
    ]
  },
  {
    year: '2028',
    label: 'Global Expansion & Advanced Intelligence',
    color: 'bg-accent-orange',
    textColor: 'text-accent-orange',
    borderColor: 'border-orange-200',
    bgColor: 'bg-orange-50',
    arrowColor: '#ea580c',
    items: [
      { quarter: 'H1', icon: Lock, status: 'Future', statusColor: 'bg-gray-100 text-gray-600', title: 'Insurance, Cybersecurity & Compliance', desc: 'Automated claims, zero-trust fleet security, AI accident risk scoring, audit trails.' },
      { quarter: 'H2', icon: Globe2, status: 'Future', statusColor: 'bg-gray-100 text-gray-600', title: 'Southeast Asia Expansion', desc: 'Multi-country, multi-currency, local tax & compliance, regional languages.' },
    ]
  },
];

const ProductRoadmap: React.FC = () => {
  return (
    <div>
      <div className="relative">
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-400 via-purple-400 to-orange-400 hidden lg:block" />

        <div className="space-y-16">
          {roadmap.map((phase, phaseIndex) => (
            <div key={phaseIndex} className="relative lg:pl-20">

              <div className={`absolute left-0 top-1 hidden lg:flex w-12 h-12 ${phase.color} rounded-full items-center justify-center shadow-lg z-10`}>
                <span className="text-white text-xs font-bold">{phase.year.slice(2)}</span>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <div className={`lg:hidden px-4 py-1.5 ${phase.color} text-white text-sm font-bold rounded-full`}>{phase.year}</div>
                <div className="hidden lg:block">
                  <span className={`text-2xl font-extrabold ${phase.textColor}`}>{phase.year}</span>
                </div>
                <span className="text-base font-semibold text-gray-700">{phase.label}</span>
              </div>

              <div className="relative">
                <div
                  className="absolute top-8 left-8 right-8 h-0.5 hidden lg:block"
                  style={{ background: `linear-gradient(to right, ${phase.arrowColor} 0%, transparent 100%)`, opacity: 0.25 }}
                />
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 lg:gap-0 items-start">
                  {phase.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start">
                      <div className={`flex-1 bg-white rounded-2xl border-2 ${phase.borderColor} p-5 hover:shadow-lg transition-all duration-300 mx-2 relative`}>
                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 ${phase.color} text-white text-[10px] font-bold rounded-full shadow`}>
                          {item.quarter}
                        </div>
                        <div className="flex items-center justify-between mb-3 mt-1">
                          <div className={`w-9 h-9 ${phase.bgColor} rounded-xl flex items-center justify-center`}>
                            <item.icon className={`w-4 h-4 ${phase.textColor}`} />
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${item.statusColor}`}>{item.status}</span>
                        </div>
                        <h3 className="text-sm font-bold text-navy-950 mb-2 leading-snug">{item.title}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                      </div>

                      {itemIndex < phase.items.length - 1 && (
                        <div className="hidden lg:flex items-center self-center mt-4 flex-shrink-0 -mx-1 z-10">
                          <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                            <path d="M0 8H16M16 8L10 2M16 8L10 14" stroke={phase.arrowColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 mx-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400 font-medium">Phase progress</span>
                  <span className={`text-xs font-bold ${phase.textColor}`}>{phaseIndex === 0 ? '25%' : '0%'}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${phase.color} rounded-full`} style={{ width: phaseIndex === 0 ? '25%' : '0%' }} />
                </div>
              </div>

              {phaseIndex < roadmap.length - 1 && (
                <div className="hidden lg:flex justify-center mt-6 -mb-8">
                  <svg width="16" height="28" viewBox="0 0 16 28" fill="none">
                    <path d="M8 0V22M8 22L2 16M8 22L14 16" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6">
        {[
          { value: '8+', label: 'Major Modules' },
          { value: '137', label: 'SCMT Features' },
          { value: '30+', label: 'AI Capabilities' },
          { value: '2028', label: 'Global Expansion' },
        ].map((s, i) => (
          <div key={i} className="text-center bg-navy-950 rounded-2xl py-6">
            <div className="text-2xl sm:text-3xl font-bold text-accent-orange mb-1">{s.value}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductRoadmap;
